import httpService from "../../services/httpService";
import { ApiEndpoints } from "../../constants/apiEndpoints";
import { PaginationMeta } from "../../types/api";

export interface SongV2Source {
  type: string;
  url: string;
  video_id: string;
}

export interface SongV2Lane {
  lane: number;
  name: string;
  midi_notes: number[];
}

export interface SongV2TempoMap {
  tick: number;
  bpm: number;
}

export interface SongV2ChartEvent {
  t0: number;
  dt: number;
  m: number;
}

export interface SongV2Chart {
  format: string;
  ppq: number;
  tempo_map: SongV2TempoMap[];
  events: SongV2ChartEvent[];
}

/** Lightweight response returned by GET /songs. */
export interface SongV2ListItem {
  songv2_id: string;
  v: number;
  title: string;
  artist: string;
  bpm: number;
  ts: string;
  duration_ms: number;
  source: SongV2Source;
  sync_ms: number;
  lookahead_ms: number;
  hit_ms: number;
  calibrate_speed: number;
  is_locked: boolean;
  created_at: string | null;
  updated_at: string | null;
}

/** Full response returned by GET /songs/{songv2_id}. */
export interface SongV2 extends SongV2ListItem {
  lanes: SongV2Lane[];
  dt: number[];
  m: number[];
  chart: SongV2Chart;
}

export interface CreateSongV2Data {
  v: number;
  title: string;
  artist: string;
  bpm: number;
  ts: string;
  duration_ms: number;
  source: SongV2Source;
  sync_ms: number;
  lookahead_ms: number;
  hit_ms: number;
  calibrate_speed: number;
  lanes: SongV2Lane[];
  dt: number[];
  m: number[];
  chart: SongV2Chart;
  is_locked?: boolean;
}

export type UpdateSongV2Data = Partial<CreateSongV2Data>;

export interface SongV2QueryParams {
  page?: number;
  limit?: number;
  search?: string;
  artist?: string;
  source_type?: string;
  is_locked?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta | null;
}

const CATALOG_BASE = ApiEndpoints.SONGS.replace(/\/$/, "");
const ADMIN_BASE = ApiEndpoints.ADMIN_SONGS.replace(/\/$/, "");

function songPath(base: string, id: string, suffix = "") {
  return `${base}/${encodeURIComponent(id)}${suffix}`;
}

function normalizeSource(value: unknown): SongV2Source {
  const source =
    value && typeof value === "object"
      ? (value as Record<string, unknown>)
      : {};
  return {
    type: typeof source.type === "string" ? source.type : "youtube",
    url: typeof source.url === "string" ? source.url : "",
    video_id:
      typeof source.video_id === "string" ? source.video_id : "",
  };
}

function normalizeListItem(item: any): SongV2ListItem {
  return {
    songv2_id: String(item?.songv2_id || ""),
    v: Number(item?.v || 1),
    title: String(item?.title || ""),
    artist: String(item?.artist || ""),
    bpm: Number(item?.bpm || 0),
    ts: String(item?.ts || "4/4"),
    duration_ms: Number(item?.duration_ms || 0),
    source: normalizeSource(item?.source),
    sync_ms: Number(item?.sync_ms || 0),
    lookahead_ms: Number(item?.lookahead_ms || 0),
    hit_ms: Number(item?.hit_ms || 0),
    calibrate_speed: Number(item?.calibrate_speed ?? 1),
    is_locked: Boolean(item?.is_locked),
    created_at:
      typeof item?.created_at === "string" ? item.created_at : null,
    updated_at:
      typeof item?.updated_at === "string" ? item.updated_at : null,
  };
}

function normalizeSong(item: any): SongV2 {
  const lightweight = normalizeListItem(item);
  const chart =
    item?.chart && typeof item.chart === "object"
      ? item.chart
      : { format: "score_v2", ppq: 480, tempo_map: [], events: [] };
  return {
    ...lightweight,
    lanes: Array.isArray(item?.lanes) ? item.lanes : [],
    dt: Array.isArray(item?.dt) ? item.dt.map(Number) : [],
    m: Array.isArray(item?.m) ? item.m.map(Number) : [],
    chart: {
      format: String(chart.format || "score_v2"),
      ppq: Number(chart.ppq || 480),
      tempo_map: Array.isArray(chart.tempo_map) ? chart.tempo_map : [],
      events: Array.isArray(chart.events) ? chart.events : [],
    },
  };
}

function asCollection(payload: unknown): SongV2ListItem[] {
  if (Array.isArray(payload)) return payload.map(normalizeListItem);
  if (!payload || typeof payload !== "object") return [];
  const record = payload as Record<string, unknown>;
  const rows = Array.isArray(record.items)
    ? record.items
    : Array.isArray(record.songs)
      ? record.songs
      : [];
  return rows.map(normalizeListItem);
}

export async function fetchSongsV2(
  params: SongV2QueryParams = {}
): Promise<PaginatedResponse<SongV2ListItem>> {
  const response = await httpService.get<
    SongV2ListItem[] | {
      items?: SongV2ListItem[];
      songs?: SongV2ListItem[];
    }
  >(CATALOG_BASE, { params });
  return {
    data: asCollection(response.data),
    meta: response.meta || null,
  };
}

export async function fetchSongV2ById(id: string): Promise<SongV2> {
  const response = await httpService.get<SongV2>(
    songPath(CATALOG_BASE, id)
  );
  return normalizeSong(response.data);
}

export async function createSongV2(
  data: CreateSongV2Data
): Promise<SongV2> {
  const response = await httpService.post<SongV2>(ADMIN_BASE, data);
  return normalizeSong(response.data);
}

export async function updateSongV2(
  id: string,
  data: UpdateSongV2Data
): Promise<SongV2> {
  const response = await httpService.put<SongV2>(
    songPath(ADMIN_BASE, id),
    data
  );
  return normalizeSong(response.data);
}

export async function deleteSongV2(id: string): Promise<void> {
  await httpService.delete(songPath(ADMIN_BASE, id));
}

/** Compatibility endpoint: V2 has no status and this returns the same song. */
export async function publishSongV2(id: string): Promise<SongV2> {
  const response = await httpService.put<SongV2>(
    songPath(ADMIN_BASE, id, "/publish")
  );
  return normalizeSong(response.data);
}

export async function searchSongsV2(
  query: string,
  params: SongV2QueryParams = {}
) {
  return fetchSongsV2({ ...params, search: query });
}

export async function getSuggestionsV2(query: string, limit = 5) {
  const response = await fetchSongsV2({ search: query, limit });
  return response.data.map((song) => song.title).filter(Boolean);
}

export async function fetchMySongsV2(params: SongV2QueryParams = {}) {
  return fetchSongsV2(params);
}

export async function fetchAssignedSongsV2(
  params: SongV2QueryParams = {}
) {
  return fetchSongsV2(params);
}
